import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["block", "children", "disabled", "flush", "height", "name", "onClick", "onSelectedChange", "padded", "readOnly", "selected", "selectionMarkLocation", "tabIndex", "type", "width"];
import devLogger from 'react-utils/devLogger';
import { useMemo, useCallback } from 'react';
import { Controllable } from '../Controllable/Controllable';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { Checkbox } from '../Checkbox';
import { RadioInput } from '../RadioInput';
import { uniqueId } from '../../utils/uniqueId';
import { stopPropagationHandler } from '../../utils/DomEvents';
import { useActive } from '../../utils/useActive';
import { useHovered } from '../../utils/useHovered';
import styled from 'foundations-theming/utils';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledSelectableBox = styled.div.withConfig({
  displayName: "SelectableBox__StyledSelectableBox",
  componentId: "sc-12d2xio-0"
})(props => {
  const theme = props.theme;
  return Object.assign({
    backgroundColor: theme.color['fill-surface-default'],
    border: `1px solid ${theme.color['border-interactive-default']}`,
    borderRadius: theme.space[100],
    cursor: 'pointer',
    display: 'inline-block',
    position: 'relative',
    userSelect: 'none',
    width: 'auto',
    textAlign: 'center',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    transition: 'all 150ms ease-out'
  }, props.$block && {
    width: '100%'
  }, props.$padded && {
    padding: '20px'
  }, props.$hovered && !props.$disabled && !props.$readOnly && {
    boxShadow: `0 0 0 1px ${theme.color['border-interactive-default']}, 0 0 ${theme.space[300]} 0 rgba(0, 163, 189, 0.3)`
  }, (props.$active || props.$selected) && {
    backgroundColor: theme.color['fill-secondary-subtle']
  }, props.$selected && {
    borderColor: theme.color['fill-secondary-default'],
    boxShadow: `0 0 0 ${theme.space[25]} ${theme.color['fill-secondary-default']}`,
    '&:hover': {
      boxShadow: `0 0 0 ${theme.space[25]} ${theme.color['fill-secondary-pressed']}, 0 0 ${theme.space[300]} 0 rgba(33, 33, 33, 0.15)`
    }
  }, props.$readOnly && {
    backgroundColor: `${theme.color['fill-tertiary-default']} !important`,
    borderColor: `${theme.color['border-core-subtle']} !important`,
    color: `${theme.color['text-core-disabled']} !important`,
    cursor: 'auto',
    '& input + span': {
      borderColor: `${theme.color['border-core-default']} !important`
    }
  }, props.$disabled && {
    backgroundColor: `${theme.color['fill-tertiary-default']} !important`,
    borderColor: `${theme.color['border-core-subtle']} !important`,
    color: `${theme.color['text-core-disabled']} !important`,
    cursor: 'not-allowed'
  }, props.$flush && {
    padding: '0 !important'
  }, props.$cssStyles);
});
const StyledInner = styled.div.withConfig({
  displayName: "SelectableBox__StyledInner",
  componentId: "sc-12d2xio-1"
})(props => {
  const theme = props.theme;
  return {
    fontSize: theme.typography['body-200'].fontSize,
    marginLeft: 0,
    whiteSpace: 'normal',
    height: props.$height ? `${props.$height}px` : 'auto',
    width: props.$width ? `${props.$width}px` : 'auto'
  };
});
const StyledSelectionMark = styled.div.withConfig({
  displayName: "SelectableBox__StyledSelectionMark",
  componentId: "sc-12d2xio-2"
})(props => {
  const theme = props.theme;
  return Object.assign({
    position: 'absolute'
  }, props.$location === 'top-right' && {
    right: theme.space[300],
    top: theme.space[400]
  }, props.$location === 'left' && {
    left: theme.space[500],
    marginTop: `-${theme.space[250]}`,
    top: '50%',
    [`& + ${StyledInner}`]: {
      paddingLeft: theme.space[600],
      marginRight: theme.space[600]
    }
  });
});
const InternalSelectableBox = props => {
  const {
      block,
      children,
      disabled = false,
      flush,
      height,
      name,
      onClick,
      onSelectedChange,
      padded = true,
      readOnly = false,
      selected = false,
      selectionMarkLocation,
      tabIndex,
      type = 'default',
      width
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const _id = useMemo(() => uniqueId('bsb-'), []);
  const handleClick = useCallback(evt => {
    if ((disabled || readOnly) && evt != null) {
      evt.preventDefault();
      evt.stopPropagation();
      return;
    }
    if (type !== 'radio') {
      onSelectedChange === null || onSelectedChange === void 0 || onSelectedChange(syntheticEvent(!selected));
    }
    if (onClick && !disabled) onClick(evt);
  }, [disabled, readOnly, type, selected, onSelectedChange, onClick]);
  const handleKeyPress = useCallback(evt => {
    if (evt.currentTarget.getAttribute('type') === 'checkbox') return;
    const {
      key
    } = evt;
    if ((key === 'Enter' || key === ' ') && !disabled) {
      evt.preventDefault();
      handleClick();
    }
  }, [disabled, handleClick]);
  const activeProps = useActive(props);
  const hoveredProps = useHovered(props);
  let renderedSelectionMark = null;
  if (type !== 'default') {
    if (type === 'checkbox') {
      renderedSelectionMark = /*#__PURE__*/_jsx(StyledSelectionMark, {
        $location: selectionMarkLocation,
        children: /*#__PURE__*/_jsx(Checkbox, {
          "aria-labelledby": _id,
          checked: selected,
          disabled: disabled,
          innerPadding: "none",
          name: name,
          onChange: handleClick,
          onClick: stopPropagationHandler,
          readOnly: readOnly
        })
      });
    }
    if (type === 'radio') {
      if (process.env.NODE_ENV !== 'production' && !name) {
        devLogger.warn({
          message: 'SelectableBox: If type is set to radio you must provide a name',
          key: 'SelectableBox: no name value'
        });
      }
      renderedSelectionMark = /*#__PURE__*/_jsx(StyledSelectionMark, {
        $location: selectionMarkLocation,
        children: /*#__PURE__*/_jsx(RadioInput, {
          "aria-labelledby": _id,
          checked: selected,
          disabled: disabled,
          name: name,
          onChange: handleClick,
          onClick: stopPropagationHandler,
          readOnly: readOnly
        })
      });
    }
  }
  const tabbableByDefault = !disabled && type === 'default';
  const defaultTabIndex = tabbableByDefault ? 0 : undefined;
  return /*#__PURE__*/_jsxs(StyledSelectableBox, Object.assign({}, rest, activeProps, hoveredProps, {
    $cssStyles: cssStyles,
    $block: block,
    $disabled: disabled,
    $flush: flush,
    $hovered: hoveredProps.hovered,
    $padded: padded,
    $readOnly: readOnly,
    $selected: selected,
    $active: activeProps.active,
    "aria-checked": selected,
    "aria-disabled": disabled,
    "aria-readonly": readOnly,
    onClick: handleClick,
    onKeyPress: handleKeyPress,
    role: type === 'radio' ? 'radio' : 'checkbox',
    tabIndex: typeof tabIndex === 'number' ? tabIndex : defaultTabIndex,
    children: [renderedSelectionMark, /*#__PURE__*/_jsx(StyledInner, {
      id: _id,
      $height: height,
      $width: width,
      children: children
    })]
  }));
};
export const UncontrolledSelectableBox = InternalSelectableBox;
export const SelectableBox = Controllable(InternalSelectableBox, ['selected']);